import { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Eye, EyeOff } from 'lucide-react'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (currentPassword: string, newPassword: string) => Promise<void>
}

export function ChangePasswordDialog({ open, onOpenChange, onSave }: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ current?: string; new?: string; confirm?: string }>({})
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false })

  const validateForm = () => {
    const newErrors: typeof errors = {}

    if (!currentPassword) {
      newErrors.current = 'Senha atual é obrigatória'
    }

    if (!newPassword) {
      newErrors.new = 'Nova senha é obrigatória'
    } else if (newPassword.length < 6) {
      newErrors.new = 'Nova senha deve ter pelo menos 6 caracteres'
    } else if (newPassword === currentPassword) {
      newErrors.new = 'Nova senha deve ser diferente da atual'
    }

    if (!confirmPassword) {
      newErrors.confirm = 'Confirmação de senha é obrigatória'
    } else if (confirmPassword !== newPassword) {
      newErrors.confirm = 'As senhas não conferem'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setIsLoading(true)
    try {
      await onSave(currentPassword, newPassword)
      handleCancel()
    } catch (error) {
      // Erro já tratado no pai
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setErrors({})
    setShowPasswords({ current: false, new: false, confirm: false })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">Alterar Senha</DialogTitle>
          <DialogDescription className="text-sm">
            Digite sua senha atual e escolha uma nova senha.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="current_password" className="text-sm font-medium">Senha Atual</Label>
            <div className="relative">
              <Input
                id="current_password"
                type={showPasswords.current ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="pr-10 text-base"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                disabled={isLoading}
              >
                {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.current && (
              <p className="text-xs md:text-sm text-destructive">{errors.current}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="new_password" className="text-sm font-medium">Nova Senha</Label>
            <div className="relative">
              <Input
                id="new_password"
                type={showPasswords.new ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="pr-10 text-base"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                disabled={isLoading}
              >
                {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.new && (
              <p className="text-xs md:text-sm text-destructive">{errors.new}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="confirm_password" className="text-sm font-medium">Confirmar Nova Senha</Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="pr-10 text-base"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                disabled={isLoading}
              >
                {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.confirm && (
              <p className="text-xs md:text-sm text-destructive">{errors.confirm}</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Alterar Senha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
